import React, { useState, useEffect } from 'react';
import { Save, Store, Truck, Gift, Languages, Boxes, Image as ImageIcon, Ruler } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { shopSettingsApi, uploadApi, unitApi } from '../api';
import LogoUploadCard from '../components/settings/LogoUploadCard';
import ThemeColorCard from '../components/settings/ThemeColorCard';
import ShopNameCard from '../components/settings/ShopNameCard';
import ShopTagCard from '../components/settings/ShopTagCard';
import DeliverySettingsCard from '../components/settings/DeliverySettingsCard';
import PaymentSettingsCard from '../components/settings/PaymentSettingsCard';
import KhrRateCard from '../components/settings/KhrRateCard';
import ContactInfoCard from '../components/settings/ContactInfoCard';
import BannerManageSection from '../components/settings/BannerManageSection';
import BannerFormDialog from '../components/settings/BannerFormDialog';
import FullReductionSettingsCard from '../components/settings/FullReductionSettingsCard';

// 语言开关支持的全部选项——这次新增了马来语、繁体中文
const LANGUAGE_OPTIONS = [
  { code: 'zh',    label: '中文' },
  { code: 'en',    label: 'English' },
  { code: 'km',    label: 'ខ្មែរ（高棉语）' },
  { code: 'ms',    label: 'Bahasa Melayu（马来语）' },
  { code: 'zh_tw', label: '繁體中文' },
];

const TABS = [
  { key: 'shop',      label: '店铺信息',   icon: Store },
  { key: 'delivery',  label: '配送与支付', icon: Truck },
  { key: 'promotion', label: '促销活动',   icon: Gift },
  { key: 'language',  label: '语言设置',   icon: Languages },
  { key: 'units',     label: '单位管理',   icon: Ruler },
  { key: 'inventory', label: '库存',       icon: Boxes },
  { key: 'banners',   label: '广告图管理', icon: ImageIcon },
];

const ShopSettingsPage = () => {
  const { setShopSettings, language } = useApp();

  const [activeTab, setActiveTab] = useState('shop');

  const [settings, setSettings] = useState({
    shop_name: '', shop_name_en: '', shop_name_km: '', shop_name_ms: '', shop_name_zh_tw: '',
    shop_tag: '', shop_tag_en: '', shop_tag_km: '', shop_tag_ms: '', shop_tag_zh_tw: '',
    logo_url: '', primary_color: '#F97316',
    phone: '', email: '', address: '', facebook: '', telegram: '',
    enable_pickup: 1, enable_delivery: 1,
    enable_khqr: 1, enable_alipay: 1, enable_usdt: 1, enable_cod: 1,
    khr_rate: 4200,
    free_shipping_threshold: '', base_delivery_fee: '',
    business_hours_start: '', business_hours_end: '',
    enable_instant_delivery: 1,
    free_shipping_for_scheduled: 0,
    stock_unlimited: 0,
    full_reduction_enabled: 0,
    full_reduction_vip_allowed: 1,
    distance_fee_enabled: 0,
    delivery_fee_store_source: 'auto',
    default_delivery_store_id: '',
    enabled_languages: ['zh', 'en', 'km'],
  });
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showBannerForm, setShowBannerForm] = useState(false);
  const [bannerForm, setBannerForm] = useState({ image_url: '', title: '', link_url: '', sort_order: 0 });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);

  // 单位管理：租户不再自己创建/编辑单位，只在这里勾选/取消勾选要用哪些——
  // 用 getStats 而不是普通的 getList，多了个 product_count，方便商家看到
  // "这个单位现在有多少商品在用"，考虑要不要停用的时候心里有数
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [togglingUnitId, setTogglingUnitId] = useState(null);

  useEffect(() => { loadSettings(); loadUnits(); }, []);

  const loadUnits = async () => {
    setLoadingUnits(true);
    try {
      const res = await unitApi.getStats();
      if (res.success) setUnits(res.data || []);
    } catch (err) {
      console.error('加载单位列表失败:', err);
    } finally {
      setLoadingUnits(false);
    }
  };

  // 按当前POS界面语言，取对应的单位名称——某个语言没填翻译，自动退回中文，
  // 不会出现空白
  const getUnitName = (unit) => {
    const fieldMap = { zh: 'name', en: 'name_en', km: 'name_km', ms: 'name_ms', zh_tw: 'name_zh_tw' };
    const field = fieldMap[language] || 'name';
    return unit[field] || unit.name;
  };

  const handleToggleUnit = async (unit) => {
    setTogglingUnitId(unit.id);
    try {
      const res = await unitApi.toggle(unit.id, { is_enabled: !unit.is_enabled });
      if (res.success) {
        setUnits(prev => prev.map(u => u.id === unit.id ? { ...u, is_enabled: !u.is_enabled } : u));
        if (res.message && res.message !== '设置已更新') {
          // 停用时如果后端提示"还有N个商品在用"，这里展示出来，不是静默切换
          alert(res.message);
        }
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '操作失败');
    } finally {
      setTogglingUnitId(null);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await shopSettingsApi.getSettings();
      if (res.success && res.data) {
        const s = res.data;
        setSettings({
          shop_name:       s.shop_name      || '',
          shop_name_en:    s.shop_name_en   || '',
          shop_name_km:    s.shop_name_km   || '',
          shop_name_ms:    s.shop_name_ms   || '',
          shop_name_zh_tw: s.shop_name_zh_tw || '',
          shop_tag:        s.shop_tag       || '',
          shop_tag_en:     s.shop_tag_en    || '',
          shop_tag_km:     s.shop_tag_km    || '',
          shop_tag_ms:     s.shop_tag_ms    || '',
          shop_tag_zh_tw:  s.shop_tag_zh_tw || '',
          logo_url:        s.logo_url       || '',
          primary_color:   s.primary_color  || '#F97316',
          phone:           s.phone          || '',
          email:           s.email          || '',
          address:         s.address        || '',
          facebook:        s.facebook       || '',
          telegram:        s.telegram       || '',
          enable_pickup:   s.enable_pickup   !== undefined ? s.enable_pickup   : 1,
          enable_delivery: s.enable_delivery !== undefined ? s.enable_delivery : 1,
          enable_khqr:     s.enable_khqr     !== undefined ? s.enable_khqr     : 1,
          enable_alipay:   s.enable_alipay   !== undefined ? s.enable_alipay   : 1,
          enable_usdt:     s.enable_usdt     !== undefined ? s.enable_usdt     : 1,
          enable_cod:      s.enable_cod      !== undefined ? s.enable_cod      : 1,
          khr_rate:        s.khr_rate       || 4200,
          free_shipping_threshold: s.free_shipping_threshold ?? '',
          base_delivery_fee:       s.base_delivery_fee ?? '',
          business_hours_start:    s.business_hours_start || '',
          business_hours_end:      s.business_hours_end   || '',
          enable_instant_delivery: s.enable_instant_delivery !== undefined ? s.enable_instant_delivery : 1,
          free_shipping_for_scheduled: s.free_shipping_for_scheduled !== undefined ? s.free_shipping_for_scheduled : 0,
          stock_unlimited: s.stock_unlimited !== undefined ? s.stock_unlimited : 0,
          full_reduction_enabled: s.full_reduction_enabled !== undefined ? s.full_reduction_enabled : 0,
          full_reduction_vip_allowed: s.full_reduction_vip_allowed !== undefined ? s.full_reduction_vip_allowed : 1,
          distance_fee_enabled: s.distance_fee_enabled !== undefined ? s.distance_fee_enabled : 0,
          delivery_fee_store_source: s.delivery_fee_store_source || 'auto',
          default_delivery_store_id: s.default_delivery_store_id || '',
          enabled_languages: Array.isArray(s.enabled_languages)
            ? s.enabled_languages
            : (typeof s.enabled_languages === 'string' ? s.enabled_languages.split(',').filter(Boolean) : ['zh', 'en', 'km']),
        });
        if (s.logo_url) setLogoPreview(s.logo_url);
        setBanners(s.banners || []);
      }
    } catch (err) {
      setError('加载设置失败');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const toggleField = (field) => {
    setSettings(prev => ({ ...prev, [field]: prev[field] ? 0 : 1 }));
  };

  // 语言开关：H5和POS已经合并成一个，至少要保留一种语言
  const toggleLanguage = (code) => {
    setSettings(prev => {
      const list = prev.enabled_languages;
      const has = list.includes(code);
      if (has && list.length === 1) {
        alert('至少要保留一种语言');
        return prev;
      }
      const next = has ? list.filter(c => c !== code) : [...list, code];
      return { ...prev, enabled_languages: next };
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('图片不能超过 5MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return settings.logo_url;
    setUploadingLogo(true);
    try {
      const res = await uploadApi.uploadProductImage(logoFile);
      if (res.success && res.data?.url) return res.data.url;
      throw new Error(res.message || '上传失败');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!settings.shop_name) { setError('店铺名称不能为空'); return; }
    if (!settings.khr_rate || settings.khr_rate < 100) { setError('KHR 汇率不能小于 100'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let logoUrl = settings.logo_url;
      if (logoFile) logoUrl = await uploadLogo();

      const res = await shopSettingsApi.updateSettings({ ...settings, logo_url: logoUrl });
      if (res.success) {
        setSuccess('保存成功！');
        setLogoFile(null);
        setSettings(prev => ({ ...prev, logo_url: logoUrl }));
        if (setShopSettings) setShopSettings(res.data);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleAddBanner = async () => {
    if (!bannerFile && !bannerForm.image_url) { alert('请上传广告图片'); return; }
    setSavingBanner(true);
    try {
      let imageUrl = bannerForm.image_url;
      if (bannerFile) {
        setUploadingBanner(true);
        const res = await uploadApi.uploadProductImage(bannerFile);
        setUploadingBanner(false);
        if (res.success && res.data?.url) imageUrl = res.data.url;
        else throw new Error(res.message || '上传失败');
      }
      const res = await shopSettingsApi.createBanner({ ...bannerForm, image_url: imageUrl });
      if (res.success) {
        setBanners(prev => [...prev, res.data]);
        setShowBannerForm(false);
        setBannerForm({ image_url: '', title: '', link_url: '', sort_order: 0 });
        setBannerFile(null);
        setBannerPreview('');
      } else {
        alert(res.message || '添加失败');
      }
    } catch (err) {
      alert(err.message || '添加失败');
    } finally {
      setSavingBanner(false);
      setUploadingBanner(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('确定删除这张广告图吗？')) return;
    try {
      const res = await shopSettingsApi.deleteBanner(id);
      if (res.success) setBanners(prev => prev.filter(b => b.id !== id));
      else alert(res.message || '删除失败');
    } catch (err) { alert(err.message || '删除失败'); }
  };

  const toggleBannerStatus = async (banner) => {
    const newStatus = banner.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await shopSettingsApi.updateBanner(banner.id, { status: newStatus });
      if (res.success) {
        setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, status: newStatus } : b));
      }
    } catch (err) { alert('操作失败'); }
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center min-h-screen">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">商家设置</h2>
          <p className="text-gray-600 mt-1">管理店铺基本信息、Logo 和广告图</p>
        </div>
        <button onClick={handleSave} disabled={saving || uploadingLogo}
          className="flex items-center space-x-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          <Save className="w-4 h-4" />
          <span>{saving ? '保存中...' : '保存设置'}</span>
        </button>
      </div>

      {error   && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">{success}</div>}

      {/* 标签页导航——沿用数据看板渠道切换那套胶囊按钮样式 */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit flex-wrap">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* 店铺信息 */}
      {activeTab === 'shop' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-6">
            <LogoUploadCard logoPreview={logoPreview} onLogoChange={handleLogoChange} />
            <ThemeColorCard color={settings.primary_color} onChange={c => updateField('primary_color', c)} />
            <ContactInfoCard settings={settings} onChange={updateField} />
          </div>
          <div className="lg:col-span-2 space-y-6">
            <ShopNameCard settings={settings} onChange={updateField} enabledLanguages={settings.enabled_languages} />
            <ShopTagCard settings={settings} onChange={updateField} enabledLanguages={settings.enabled_languages} />
          </div>
        </div>
      )}

      {/* 配送与支付 */}
      {activeTab === 'delivery' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-6">
            <KhrRateCard rate={settings.khr_rate} onChange={v => updateField('khr_rate', v)} />
          </div>
          <div className="lg:col-span-2 space-y-6">
            <DeliverySettingsCard settings={settings} onToggle={toggleField} onChange={updateField} />
            <PaymentSettingsCard settings={settings} onToggle={toggleField} />
          </div>
        </div>
      )}

      {/* 促销活动 */}
      {activeTab === 'promotion' && (
        <div className="max-w-2xl">
          <FullReductionSettingsCard settings={settings} onToggle={toggleField} />
        </div>
      )}

      {/* 语言设置 */}
      {activeTab === 'language' && (
        <div className="max-w-2xl space-y-6">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
            <p>控制客户在H5商城、店员在POS后台能看到哪几个语言选项——不影响商品/分类名称的翻译内容本身。</p>
          </div>

          <div className="bg-white rounded-xl shadow p-5">
            <h3 className="font-semibold text-gray-800 mb-1">启用的语言</h3>
            <p className="text-sm text-gray-500 mb-3">同时控制H5商城和POS后台的语言切换选项</p>
            <div className="space-y-2">
              {LANGUAGE_OPTIONS.map(lang => (
                <label key={lang.code} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox"
                    checked={settings.enabled_languages.includes(lang.code)}
                    onChange={() => toggleLanguage(lang.code)}
                    className="w-4 h-4 rounded" />
                  <span className="text-sm text-gray-700">{lang.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 单位管理 */}
      {activeTab === 'units' && (
        <div className="max-w-4xl space-y-4">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
            <p>勾选要在商品编辑时使用的计量单位——单位本身（常用清单、特殊单位）由平台统一维护，这里只控制"用不用"，不需要自己新建或翻译。</p>
          </div>

          {loadingUnits ? (
            <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400 text-sm">加载中...</div>
          ) : units.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400 text-sm">暂无可用单位</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {units.map(unit => {
                const isBusy = togglingUnitId === unit.id;
                return (
                  <div
                    key={unit.id}
                    className={`relative rounded-xl border p-4 transition-all ${
                      unit.is_enabled
                        ? 'border-blue-200 bg-blue-50/50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`text-base font-semibold truncate ${unit.is_enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                          {getUnitName(unit)}
                        </p>
                        {unit.product_count > 0 && (
                          <p className="text-xs text-gray-400 mt-1">{unit.product_count} 个商品在用</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleToggleUnit(unit)}
                        disabled={isBusy}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                          unit.is_enabled ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            unit.is_enabled ? 'translate-x-4.5' : 'translate-x-1'
                          }`}
                          style={{ transform: unit.is_enabled ? 'translateX(18px)' : 'translateX(4px)' }}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 库存 */}
      {activeTab === 'inventory' && (
        <div className="max-w-2xl">
          {/* 不限库存：全局开关，影响所有H5线上下单是否校验/扣减总店库存，
              用警示色突出，避免店家没意识到这是个影响面很大的开关就随手点了 */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span>⚠️</span>
                  不限库存模式
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  开启后，H5线上下单不再检查、也不会扣减总店的库存数量，客户随时可以下单。
                  关闭时会恢复正常的库存校验——但开启期间实际卖出的数量不会体现在库存数字上，
                  关闭前建议先手动盘点核对一次库存。
                </p>
              </div>
              <button
                onClick={() => toggleField('stock_unlimited')}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ml-4 ${
                  settings.stock_unlimited ? 'bg-amber-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.stock_unlimited ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 广告图管理 */}
      {activeTab === 'banners' && (
        <BannerManageSection
          banners={banners}
          onAddClick={() => {
            setShowBannerForm(true);
            setBannerForm({ image_url: '', title: '', link_url: '', sort_order: banners.length });
            setBannerFile(null);
            setBannerPreview('');
          }}
          onToggleStatus={toggleBannerStatus}
          onDelete={handleDeleteBanner}
        />
      )}

      {showBannerForm && (
        <BannerFormDialog
          form={bannerForm}
          onFormChange={(field, value) => setBannerForm(prev => ({ ...prev, [field]: value }))}
          preview={bannerPreview}
          onFileChange={handleBannerFileChange}
          uploading={uploadingBanner}
          saving={savingBanner}
          onConfirm={handleAddBanner}
          onClose={() => setShowBannerForm(false)}
        />
      )}
    </div>
  );
};

export default ShopSettingsPage;